import React, { useState, useCallback } from 'react'
import {
  Plus,
  Search,
  Settings2,
  Trash2,
  Upload,
  ArrowRightLeft,
  Package,
  Eye,
  ToggleRight,
  ToggleLeft,
  Edit,
  RotateCcw,
  ImageIcon,
  Barcode,
  Download,
  MoreHorizontal
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { SearchFilter } from '../components/ui/SearchFilter'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog'
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu'
import { ActionMenu } from '../components/ui/ActionMenu'

import { DetailModal } from '../components/inventory/modals/DetailModal'
import { AdjustModal } from '../components/inventory/modals/AdjustModal'
import { EditProductModal } from '../components/inventory/modals/EditProductModal'
import { ImportModal } from '../components/inventory/modals/ImportModal'
import { TransferModal } from '../components/inventory/modals/TransferModal'
import { ReceiveConfirmModal } from '../components/inventory/modals/ReceiveConfirmModal'
import { SendReviewModal } from '../components/inventory/modals/SendReviewModal'
import { AddProductModal } from '../components/inventory/modals/AddProductModal'
import { toIsoDateTime } from '../components/inventory/pricing-tier-form'
import { BranchStockModal } from '../components/inventory/modals/BranchStockModal'
import { ExportModal, ExportFormat, ExportFilters } from '../components/ui/ExportModal'
import { reportService } from '../services/report.service'
import { BatchesModal } from '../components/inventory/modals/BatchesModal'
import { useAuthStore } from '../store/auth/auth.store'

import {
  productService,
  NewProduct,
  StoreProductPayload,
  ProductListItem,
  StockAdjustmentPayload,
} from '../services/product.service'
import { TransferListItem } from '../services/transfer.service'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/app/lib/http/client'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Mock pending transfers
//const pendingSends: PendingTransfer[] = []
//const pendingReceives: PendingTransfer[] = []

const ITEMS_PER_PAGE = 10

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function Inventory() {
  const queryClient = useQueryClient()

  // ── Filtros ───────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterCategoryId, setFilterCategoryId] = useState<string>('')
  const [filterDepartmentId, setFilterDepartmentId] = useState<string>('')
  const [filterProductTypeId, setFilterProductTypeId] = useState<string>('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // ── Selección ─────────────────────────────────────────────────────────────
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [selectedProductName, setSelectedProductName] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [toggleStatusConfirmOpen, setToggleStatusConfirmOpen] = useState(false)
  const [pendingToggleItem, setPendingToggleItem] = useState<ProductListItem | null>(null)

  // ── Modales ───────────────────────────────────────────────────────────────
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAdjustModal, setShowAdjustModal] = useState(false)
  const [showAddProductModal, setShowAddProductModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showReceiveConfirmModal, setShowReceiveConfirmModal] = useState(false)
  const [showSendReviewModal, setShowSendReviewModal] = useState(false)
  const [showBranchStockModal, setShowBranchStockModal] = useState(false)
  const [showBatchesModal, setShowBatchesModal] = useState(false)

  // Export State
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const [selectedTransfer, setSelectedTransfer] = useState<TransferListItem | null>(null)
  const [selectedProductForStock, setSelectedProductForStock] = useState<ProductListItem | null>(null)
  const [selectedProductForBatches, setSelectedProductForBatches] = useState<ProductListItem | null>(null)
  const [selectedProductForAdjust, setSelectedProductForAdjust] = useState<ProductListItem | null>(null)

  const { activeBranchId, canAction } = useAuthStore()
  const canCreateCatalog = canAction('inventory.catalog.create')
  const canEditCatalog = canAction('inventory.catalog.edit')
  const canDeleteCatalog = canAction('inventory.catalog.delete')
  const canChangeCatalogStatus = canAction('inventory.catalog.status')
  const canUseCatalogTransfers = canAction('inventory.catalog-transfer.view')
  const canExportCatalog = canAction('inventory.catalog-export.export')
  const canImportCatalog = canAction('inventory.catalog-import.import')

  // ── Query: Lista de productos ─────────────────────────────────────────────
  const queryParams = {
    page: currentPage,
    per_page: ITEMS_PER_PAGE,
    search: searchQuery || undefined,
    is_active:
      filterStatus === 'all'
        ? undefined
        : filterStatus === 'active'
          ? true
          : false,
    category_id: filterCategoryId ? Number(filterCategoryId) : undefined,
    department_id: filterDepartmentId ? Number(filterDepartmentId) : undefined,
    product_type_id: filterProductTypeId ? Number(filterProductTypeId) : undefined,
    // Envía ?trashed=true para obtener los eliminados (soft-deleted)
    trashed: showDeleted ? ('true' as const) : undefined,
  }

  const { data: productsResponse, isLoading, isFetching } = useQuery({
    queryKey: ['products', queryParams],
    queryFn: () => productService.getProducts(queryParams),
    placeholderData: (prev) => prev,
  })

  const products: ProductListItem[] = productsResponse?.data ?? []
  const paginationMeta = {
    total: productsResponse?.total ?? 0,
    from: productsResponse?.from ?? 0,
    to: productsResponse?.to ?? 0,
    last_page: productsResponse?.last_page ?? 1,
  }

  // ── Handlers de filtros ───────────────────────────────────────────────────
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }, [])

  const handleStatusChange = (value: string) => {
    setFilterStatus(value as 'all' | 'active' | 'inactive')
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearchQuery('')
    setFilterStatus('all')
    setFilterCategoryId('')
    setFilterDepartmentId('')
    setShowDeleted(false)
    setCurrentPage(1)
  }

  // ── Abrir modales ─────────────────────────────────────────────────────────
  const openDetail = (id: number) => {
    setSelectedProductId(id)
    setShowDetailModal(true)
  }

  const openEdit = (id: number) => {
    if (!canEditCatalog) {
      toast.error('No tienes permiso para editar productos')
      return
    }

    setSelectedProductId(id)
    setShowEditModal(true)
  }

  const openAdjust = (item: ProductListItem) => {
    if (!canEditCatalog) {
      toast.error('No tienes permiso para ajustar productos')
      return
    }

    setSelectedProductId(item.id)
    setSelectedProductForAdjust(item)
    setShowAdjustModal(true)
  }

  // ── Mutation: Toggle status ────────────────────────────────────────────────
  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      productService.toggleStatus(id, isActive),
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success(isActive ? 'Producto activado' : 'Producto desactivado')
    },
    onError: () => toast.error('Error al cambiar el estado del producto'),
  })

  const handleToggleStatusClick = (item: ProductListItem) => {
    if (!canChangeCatalogStatus) {
      toast.error('No tienes permiso para cambiar el estado de productos')
      return
    }

    setPendingToggleItem(item)
    setToggleStatusConfirmOpen(true)
  }

  const executeToggleStatus = () => {
    if (!pendingToggleItem || !canChangeCatalogStatus) return
    toggleStatusMutation.mutate({ id: pendingToggleItem.id, isActive: !pendingToggleItem.is_active })
    setToggleStatusConfirmOpen(false)
    setPendingToggleItem(null)
  }

  // ── Mutation: Soft-delete ────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: number) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setDeleteConfirmOpen(false)
      setSelectedProductId(null)
      toast.success('Producto eliminado (puede restaurarse desde "Eliminados")')
    },
    onError: () => toast.error('Error al eliminar el producto'),
  })

  const handleDeleteClick = (item: ProductListItem) => {
    if (!canDeleteCatalog) {
      toast.error('No tienes permiso para eliminar productos')
      return
    }

    setSelectedProductId(item.id)
    setSelectedProductName(item.name)
    setDeleteConfirmOpen(true)
  }

  // ── Mutation: Restore ────────────────────────────────────────────────────
  const restoreMutation = useMutation({
    mutationFn: (id: number) => productService.restoreProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Producto restaurado correctamente')
    },
    onError: () => toast.error('Error al restaurar el producto'),
  })

  // ── Mutation: Agregar producto ────────────────────────────────────────────
  const addProductMutation = useMutation({
    mutationFn: async (payload: StoreProductPayload) => {
      const response = await productService.createProduct(payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setShowAddProductModal(false)
      toast.success('Producto agregado correctamente')
    },
    onError: () => toast.error('Error al agregar el producto'),
  })

  const handleAddProduct = async (product: NewProduct) => {
    if (!canCreateCatalog) {
      toast.error('No tienes permiso para crear productos')
      return
    }


    const payload: StoreProductPayload = {
      name: product.name,
      barcode: product.barcode,
      description: product.description,
      category_id: Number(product.categoryId),
      department_id: Number(product.departmentId),
      product_type_id: Number(product.productTypeId),
      unit_id: Number(product.unitId),
      is_active: true,
      sat_product_key_id: product.satProductKeyId ? Number(product.satProductKeyId) : undefined,
      sat_product_key_code: product.satProductKeyCode,
      sat_product_key_name: product.satProductKeyName,
      tax_ids: product.taxId ? [Number(product.taxId)] : [],
      price: {
        cost_without_taxes: Number(product.costWithoutTaxes) || 0,
        cost_with_taxes: Number(product.costWithTaxes) || 0,
        profit_percentage: Number(product.profitPercentage) || 0,
      },
      batch: product.requiresLot
        ? { batch_number: product.batchNumber, expiration_date: product.expirationDate }
        : undefined,
      heroImageFile: product.heroImage?.file,
      galleryFiles: product.galleryImages.map((img) => img.file),
      price_tiers: product.pricingTiers.map((t) => ({
        min_quantity: Number(t.minQuantity),
        profit_percentage: Number(t.profitPercentage),
        discount_percentage: Number(t.discountPercentage),
        starts_at: toIsoDateTime(t.startsAt),
        ends_at: toIsoDateTime(t.endsAt),
        is_active: t.isActive,
      })),
    }
    await addProductMutation.mutateAsync(payload)
  }

  // ── Mutation: Ajuste de stock ─────────────────────────────────────────────
  const adjustStockMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: StockAdjustmentPayload }) =>
      productService.adjustStock(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product', selectedProductId] })
      setShowAdjustModal(false)
      toast.success('Ajuste de stock aplicado')
    },
    onError: () => toast.error('Error al ajustar el stock'),
  })

  const handleApplyAdjustment = (data: {
    item: string
    type: 'add' | 'subtract'
    quantity: string
    reason: string
  }) => {
    if (!selectedProductId || !canEditCatalog) return
    adjustStockMutation.mutate({
      id: selectedProductId,
      payload: { type: data.type, quantity: Number(data.quantity), reason: data.reason },
    })
  }

  // ── Mutation: Actualizar Thresholds ───────────────────────────────────────
  const updateThresholdsMutation = useMutation({
    mutationFn: ({ id, sucursalId, min, max }: { id: number; sucursalId: number; min: number; max: number }) =>
      productService.updateStockThresholds(id, { sucursal_id: sucursalId, stock_min: min, stock_max: max }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Configuración de stock guardada')
    },
    onError: () => toast.error('Error al guardar configuración de stock'),
  })

  const handleUpdateThresholds = async (productId: number, sucursalId: number, min: number, max: number) => {
    if (!canEditCatalog) {
      toast.error('No tienes permiso para editar stock de productos')
      return
    }

    await updateThresholdsMutation.mutateAsync({ id: productId, sucursalId, min, max })
  }

  // ── Transfer handlers ──────────────────────────────────────────────────────
  const handleSendReview = (transfer: TransferListItem) => {
    setSelectedTransfer(transfer)
    setShowTransferModal(false)
    setShowSendReviewModal(true)
  }
  const handleReceiveConfirm = (transfer: TransferListItem) => {
    setSelectedTransfer(transfer)
    setShowTransferModal(false)
    setShowReceiveConfirmModal(true)
  }
  const handleConfirmReceive = () => {
    setShowReceiveConfirmModal(false)
    setSelectedTransfer(null)
  }
  const handleConfirmSend = () => {
    setShowSendReviewModal(false)
    setSelectedTransfer(null)
  }

  const handleTransferClick = () => {
    if (!canUseCatalogTransfers) {
      toast.error('No tienes permiso para consultar transferencias de catalogo')
      return
    }

    setShowTransferModal(true)
  }

  const handleImportClick = () => {
    if (!canImportCatalog) {
      toast.error('No tienes permiso para importar productos')
      return
    }

    setShowImportModal(true)
  }

  const handleRestoreClick = (id: number) => {
    if (!canChangeCatalogStatus) {
      toast.error('No tienes permiso para restaurar productos')
      return
    }

    restoreMutation.mutate(id)
  }

  const handleExportClick = () => {
    if (!canExportCatalog) {
      toast.error('No tienes permiso para exportar productos')
      return
    }

    setIsExportModalOpen(true)
  }

  const handleExecuteExport = async (format: ExportFormat, filters?: ExportFilters) => {
    if (!canExportCatalog) {
      toast.error('No tienes permiso para exportar productos')
      return
    }

    if (format !== 'excel') {
      toast.error('Actualmente solo soportamos exportación en Excel')
      return
    }

    setIsExporting(true)
    const loadingToastId = toast.info('Exportando productos...')

    try {
      const payload: any = {
        stock_mode: 'all_sucursals'
      }

      if (filters?.limitType === 'custom' && filters.customLimit) {
        payload.limit = parseInt(filters.customLimit, 10)
      }

      // Filtros del modal
      if (filters?.category_id && filters.category_id !== 'all') payload.category_ids = [parseInt(filters.category_id, 10)]
      if (filters?.department_id && filters.department_id !== 'all') payload.department_ids = [parseInt(filters.department_id, 10)]
      if (filters?.product_type_id && filters.product_type_id !== 'all') payload.product_type_ids = [parseInt(filters.product_type_id, 10)]

      if (filters?.created_from) payload.created_from = filters.created_from
      if (filters?.created_to) payload.created_to = filters.created_to
      if (filters?.updated_from) payload.updated_from = filters.updated_from
      if (filters?.updated_to) payload.updated_to = filters.updated_to

      // Búsqueda global (opcional mantenerla desde la tabla)
      if (searchQuery) payload.search = searchQuery

      const exportResponse = await reportService.createProductExport(payload)
      const exportId = exportResponse.id

      // Cerrar el modal inmediatamente para que el usuario vea el toast como antes
      setIsExportModalOpen(false)
      setIsExporting(false)

      const checkStatus = setInterval(async () => {
        try {
          const statusRes = await reportService.getProductExportStatus(exportId)
          if (statusRes.status === 'completed') {
            clearInterval(checkStatus)
            toast.success('Exportación completada', { id: loadingToastId })

            const blob = await reportService.downloadProductExport(exportId)
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `productos_export_${new Date().toISOString()}.xlsx`)
            document.body.appendChild(link)
            link.click()
            link.parentNode?.removeChild(link)

          } else if (statusRes.status === 'failed') {
            clearInterval(checkStatus)
            setIsExporting(false)
            toast.error(statusRes.error_message || 'Fallo en la exportación', { id: loadingToastId })
          }
        } catch (error) {
          clearInterval(checkStatus)
          setIsExporting(false)
          toast.error('Error al revisar el estado de exportación', { id: loadingToastId })
        }
      }, 2000)

    } catch (error) {
      setIsExporting(false)
      toast.error('Error al iniciar la exportación', { id: loadingToastId })
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────────
  const hasActiveFilters =
    searchQuery || filterStatus !== 'all' || filterCategoryId || filterDepartmentId || filterProductTypeId

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <div className="space-y-6 p-6 w-full max-w-full min-w-0">
        {/* ── Header ── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Productos
              {showDeleted && (
                <span className="ml-2 text-sm font-normal text-red-500">(eliminados)</span>
              )}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {paginationMeta.total > 0
                ? `${paginationMeta.total} productos${showDeleted ? ' eliminados' : ''}`
                : 'Gestiona el catálogo de productos'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Ver eliminados toggle */}
            <button
              onClick={() => { setShowDeleted(!showDeleted); setCurrentPage(1) }}
              title={showDeleted ? 'Ver activos' : 'Ver eliminados'}
              className={`flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors ${showDeleted
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
            >
              <Trash2 className="h-4 w-4" />
              {showDeleted ? 'Ver activos' : 'Eliminados'}
            </button>
            {canUseCatalogTransfers && (
              <Button variant="outline" onClick={handleTransferClick}>
                <ArrowRightLeft className="mr-2 h-4 w-4" /> Transferencias
              </Button>
            )}
            {canImportCatalog && (
              <Button variant="outline" onClick={handleImportClick}>
                <Upload className="mr-2 h-4 w-4" /> Importar
              </Button>
            )}
            {canExportCatalog && (
              <Button variant="outline" onClick={handleExportClick}>
                <Download className="mr-2 h-4 w-4" /> Exportar
              </Button>
            )}
            {!showDeleted && canCreateCatalog && (
              <Button onClick={() => setShowAddProductModal(true)} className="bg-primary hover:bg-primary/80 cursor-pointer">
                <Plus className="mr-2 h-4 w-4" /> Agregar Producto
              </Button>
            )}
          </div>
        </div>

        {/* ── Filtros ── */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {/* Búsqueda */}
            <div className="sm:col-span-2 lg:col-span-1">
              <SearchFilter
                placeholder="Nombre o código de barras..."
                onSearch={handleSearch}
                initialValue={searchQuery}
              />
            </div>

            {/* Categoría */}
            <CatalogSelect
              catalog="categories"
              placeholder="Todas las categorías"
              value={filterCategoryId}
              onChange={(val: string) => {
                setFilterCategoryId(val === filterCategoryId ? '' : val)
                setCurrentPage(1)
              }}
            />

            {/* Departamento */}
            <CatalogSelect
              catalog="departments"
              placeholder="Todos los departamentos"
              value={filterDepartmentId}
              onChange={(val: string) => {
                setFilterDepartmentId(val === filterDepartmentId ? '' : val)
                setCurrentPage(1)
              }}
            />

            {/* Tipo de Producto */}
            <CatalogSelect
              catalog="product_types"
              placeholder="Tipos de producto"
              value={filterProductTypeId}
              onChange={(val: string) => {
                setFilterProductTypeId(val === filterProductTypeId ? '' : val)
                setCurrentPage(1)
              }}
            />

            {/* Estado */}
            <div className="flex items-center gap-2">
              <Select value={filterStatus} onValueChange={handleStatusChange}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="active">Activos</SelectItem>
                  <SelectItem value="inactive">Inactivos</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="shrink-0 rounded px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  title="Limpiar filtros"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Tabla ── */}
        {isLoading ? (
          <TableSkeleton
            headers={['Imagen', 'Código', 'Producto', 'Stock', 'Categoría', 'Departamento', 'Tipo', 'Precio', 'Estado', 'Acciones']}
            columns={['avatar-text', 'text', 'text-two-lines', 'text', 'text', 'text', 'text', 'text', 'badge', 'actions']}
            rows={8}
          />
        ) : (
          <div className={`w-full max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-opacity ${isFetching ? 'opacity-60' : 'opacity-100'}`}>
            <div className="w-full overflow-x-auto">
              <table className="w-full whitespace-nowrap">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Imagen</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                      <div className="flex items-center gap-1"><Barcode className="h-3.5 w-3.5" /> Código</div>
                    </th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Producto</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Stock</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Categoría</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Departamento</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Tipo</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Precio</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Estado</th>
                    <th className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 sticky bg-slate-50 right-0 border-l border-slate-200 z-10 shadow-lg">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {products.length > 0 ? (
                    products.map((item) => (
                      <tr
                        key={item.id}
                        className={`transition-colors ${showDeleted ? 'bg-red-50/30 hover:bg-red-50' : 'hover:bg-slate-50'
                          }`}
                      >
                        {/* Imagen */}
                        <td className="px-2 py-4">
                          {item.hero_image ? (
                            <img
                              src={item.hero_image}
                              alt={item.name}
                              className="h-12 w-12 rounded-lg object-cover border border-slate-100 shadow-sm"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                              <ImageIcon className="h-5 w-5 text-slate-400" />
                            </div>
                          )}
                        </td>

                        {/* Barcode */}
                        <td className="px-2 py-4">
                          {item.barcode ? (
                            <span className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
                              {item.barcode}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>

                        {/* Nombre */}
                        <td className="px-2 py-4">
                          <p className={`font-medium table-cell-truncate text-sm ${showDeleted ? 'text-slate-400 line-through' : 'text-slate-900'}`} title={item.name}>
                            {item.name}
                          </p>
                          {item.description && (
                            <p className="mt-0.5 table-cell-truncate text-xs text-slate-500" title={item.description}>{item.description}</p>
                          )}
                        </td>

                        {/* Stock */}
                        <td className="px-2 py-4">
                          <div
                            className={`group flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm transition-all ${canEditCatalog ? 'cursor-pointer hover:border-primary/50 hover:bg-primary/10 hover:shadow-md active:bg-primary/15' : ''
                              }`}
                            onClick={() => {
                              if (!canEditCatalog) return
                              setSelectedProductForStock(item)
                              setShowBranchStockModal(true)
                            }}
                            title={canEditCatalog ? "Gestionar stock y mínimos/máximos por sucursal" : undefined}
                          >
                            {(() => {
                              const currentStockObj = item.stocks?.find((s) => Number(s.sucursal_id) === Number(activeBranchId))
                              const currentStock = currentStockObj ? currentStockObj.stock : 0
                              const isLowStock = currentStockObj && currentStock <= currentStockObj.stock_min

                              const otherBranchesCount = (item.stocks?.length || 0) - (currentStockObj ? 1 : 0)

                              return (
                                <>
                                  <div className="flex flex-col">
                                    <span className={`text-base font-bold ${isLowStock ? 'text-red-600' : 'text-slate-800'}`}>
                                      {currentStock} u.
                                    </span>
                                    {otherBranchesCount > 0 && (
                                      <span className="mt-0.5 text-[10px] font-medium text-slate-500 group-hover:text-primary transition-colors">
                                        En +{otherBranchesCount} suc.
                                      </span>
                                    )}
                                    {(!item.stocks || item.stocks.length === 0) && (
                                      <span className="mt-0.5 text-[10px] text-slate-400 group-hover:text-primary">Sin registros</span>
                                    )}
                                  </div>
                                  {canEditCatalog && (
                                    <div className="rounded-md bg-slate-100 p-1.5 text-slate-400 opacity-0 transition-all group-hover:bg-primary/10 group-hover:text-primary group-hover:opacity-100">
                                      <Settings2 className="h-4 w-4" />
                                    </div>
                                  )}
                                </>
                              )
                            })()}
                          </div>
                        </td>

                        {/* Categoría */}
                        <td className="px-2 py-4 text-sm text-slate-700">{item.category?.name ?? '—'}</td>

                        {/* Departamento */}
                        <td className="px-2 py-4 text-sm text-slate-700">{item.department?.name ?? '—'}</td>

                        {/* Tipo */}
                        <td className="px-2 py-4 text-sm text-slate-700">{item.product_type?.name ?? '—'}</td>

                        {/* Precio */}
                        <td className="px-2 py-4">
                          {item.price ? (
                            <div>
                              <p className="font-semibold text-slate-900">
                                ${Number(item.price.final_price ?? item.price.sale_price ?? 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-slate-500">
                                Costo: ${Number(item.price.cost_without_taxes).toFixed(2)}
                              </p>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </td>

                        {/* Estado */}
                        <td className="px-2 py-4">
                          {showDeleted ? (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
                              Eliminado
                            </span>
                          ) : (
                            <Badge className={item.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}>
                              {item.is_active ? 'Activo' : 'Inactivo'}
                            </Badge>
                          )}
                        </td>

                        {/* Acciones */}
                        <td className="px-6 py-4 bg-white sticky right-0 border-l border-slate-200 z-10 shadow-lg">
                          <div className="flex items-center gap-1">
                            {showDeleted ? (
                              /* Solo restaurar cuando se ven eliminados */
                              <button
                                onClick={() => handleRestoreClick(item.id)}
                                disabled={restoreMutation.isPending}
                                hidden={!canChangeCatalogStatus}
                                className="flex items-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium text-green-700 hover:bg-green-50"
                                title="Restaurar producto"
                              >
                                <RotateCcw className="h-3.5 w-3.5" /> Restaurar
                              </button>
                            ) : (
                              <ActionMenu>
                                <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => openDetail(item.id)}>
                                  <Eye className="mr-2 h-4 w-4 text-slate-500" /> Ver detalles
                                </DropdownMenuItem>
                                {canEditCatalog && (
                                  <DropdownMenuItem onClick={() => openEdit(item.id)}>
                                    <Edit className="mr-2 h-4 w-4 text-primary" /> Editar
                                  </DropdownMenuItem>
                                )}
                                {canChangeCatalogStatus && (
                                  <DropdownMenuItem onClick={() => handleToggleStatusClick(item)}>
                                    {item.is_active ? (
                                      <><ToggleRight className="mr-2 h-4 w-4 text-green-600" /> Desactivar</>
                                    ) : (
                                      <><ToggleLeft className="mr-2 h-4 w-4 text-slate-400" /> Activar</>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                {canEditCatalog && (
                                  <DropdownMenuItem onClick={() => openAdjust(item)}>
                                    <Settings2 className="mr-2 h-4 w-4 text-orange-500" /> Ajustar stock
                                  </DropdownMenuItem>
                                )}
                                {canEditCatalog && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        if (item.product_type?.requires_lot) {
                                          setSelectedProductForBatches(item)
                                          setShowBatchesModal(true)
                                        }
                                      }}
                                      disabled={!item.product_type?.requires_lot}
                                      title={!item.product_type?.requires_lot ? "Este tipo de producto no requiere lotes" : ""}
                                    >
                                      <Package className={`mr-2 h-4 w-4 ${item.product_type?.requires_lot ? 'text-blue-500' : 'text-slate-400'}`} /> Gestionar Lotes
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {canDeleteCatalog && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteClick(item)} className="text-red-600 focus:bg-red-50 focus:text-red-600">
                                      <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </ActionMenu>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="px-6 py-16 text-center">
                        <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                        <p className="text-slate-500">
                          {showDeleted
                            ? 'No hay productos eliminados'
                            : searchQuery
                              ? `Sin resultados para "${searchQuery}"`
                              : 'No hay productos registrados'}
                        </p>
                        {searchQuery && (
                          <p className="mt-1 text-xs text-slate-400">Intenta buscar por nombre o código de barras</p>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {paginationMeta.total > 0 && (
              <GlobalPaginator
                totalItems={paginationMeta.total}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                from={paginationMeta.from}
                to={paginationMeta.to}
              />
            )}
          </div>
        )}

        {/* ── Modales ── */}

        {/* Detalle del producto */}
        <DetailModal
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
          productId={selectedProductId}
        />

        {/* Edición completa */}
        <EditProductModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          productId={selectedProductId}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['products'] })}
        />

        {/* Ajuste de stock */}
        <AdjustModal
          open={showAdjustModal}
          onOpenChange={setShowAdjustModal}
          selectedItem={
            selectedProductForAdjust
              ? ({
                id: selectedProductForAdjust.id,
                name: selectedProductForAdjust.name,
                stock: selectedProductForAdjust.stocks?.find((s) => Number(s.sucursal_id) === Number(activeBranchId))?.stock ?? 0,
                minStock: selectedProductForAdjust.stocks?.find((s) => Number(s.sucursal_id) === Number(activeBranchId))?.stock_min ?? 0,
                category: selectedProductForAdjust.category?.name ?? '',
                stockCentro: 0,
                stockNorte: 0,
                stockSur: 0,
                price: 0,
                cost: 0,
                branch: '',
                lot: '',
                expiry: '',
                lastMovement: '',
                supplier: '',
              } as any)
              : null
          }
          onApplyAdjustment={handleApplyAdjustment}
        />

        {/* Agregar producto */}
        <AddProductModal
          open={showAddProductModal}
          onOpenChange={setShowAddProductModal}
          onAddProduct={handleAddProduct}
        />

        {/* Gestión de stock multi-sucursal */}
        <BranchStockModal
          open={showBranchStockModal}
          onOpenChange={setShowBranchStockModal}
          product={selectedProductForStock}
          onUpdateThresholds={handleUpdateThresholds}
        />

        {/* Gestión de Lotes */}
        <BatchesModal
          open={showBatchesModal}
          onOpenChange={setShowBatchesModal}
          productId={selectedProductForBatches?.id ?? null}
          productName={selectedProductForBatches?.name ?? ''}
        />

        {/* Transferencias */}
        <TransferModal
          open={showTransferModal}
          onOpenChange={setShowTransferModal}
          onSendReview={handleSendReview}
          onReceiveConfirm={handleReceiveConfirm}
        />

        <ImportModal open={showImportModal} onOpenChange={setShowImportModal} />

        <ReceiveConfirmModal
          open={showReceiveConfirmModal}
          onOpenChange={setShowReceiveConfirmModal}
          selectedTransfer={selectedTransfer}
          onConfirm={handleConfirmReceive}
        />

        <SendReviewModal
          open={showSendReviewModal}
          onOpenChange={setShowSendReviewModal}
          selectedTransfer={selectedTransfer}
          onConfirm={handleConfirmSend}
        />

        {/* ── Modal: Confirmar Eliminar Producto ── */}
        <ConfirmationModal
          isOpen={deleteConfirmOpen}
          onClose={() => { setDeleteConfirmOpen(false); setSelectedProductId(null) }}
          onConfirm={() => selectedProductId && canDeleteCatalog && deleteMutation.mutate(selectedProductId)}
          preset="delete"
          itemName={`producto "${selectedProductName}"`}
        />

        {/* ── Modal: Confirmar Cambio de Estado ── */}
        <ConfirmationModal
          isOpen={toggleStatusConfirmOpen}
          onClose={() => { setToggleStatusConfirmOpen(false); setPendingToggleItem(null) }}
          onConfirm={executeToggleStatus}
          preset="toggleStatus"
          itemName="producto"
        />
      </div>

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExecuteExport}
        title="Exportar Productos"
        isProductExport
        isExporting={isExporting}
        allowedFormats={['excel']}
      />
    </>
  )
}
